import {invalidatePageQueries} from "@mytour/platform/invalidatePageQueries";
import type {
  CreateTextDocumentPayload,
  DocumentResponse,
  UpdateTextDocumentPayload,
} from "@mytour/shared";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {completeDocumentUploadManifest} from "../creation/documentUploadManifest";
import {documentApiService} from "./documentApi.service.js";
import {
  type DocumentUploadStateCallback,
  type UploadFileDocumentItem,
  uploadDocumentQueue,
} from "./documentUploadQueue";
export type {UploadFileDocumentItem, UploadFileDocumentsResult} from "./documentUploadQueue";

export const documentKeysStore = {
  root: ["document"] as const,
  customerDocuments: (customerId: string) =>
    [...documentKeysStore.root, "list", "customer", customerId] as const,
  downloadUrl: (documentId: string) =>
    [...documentKeysStore.root, "downloadUrl", documentId] as const,
};

export function useCustomerDocuments(customerId: string | undefined, enabled = true) {
  const {data, isLoading, isFetching, refetch} = useQuery({
    queryKey: documentKeysStore.customerDocuments(customerId as string),
    queryFn: () => documentApiService.listCustomerDocuments(customerId as string),
    enabled: enabled && !!customerId,
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000,
  });

  return {
    documents: data,
    isDocumentsLoading: isLoading,
    isDocumentsFetching: isFetching,
    refetchDocuments: refetch,
  };
}

export function useCreateTextDocument() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: (payload: CreateTextDocumentPayload) =>
      documentApiService.createTextDocument(payload),
    async onSuccess(createdDocument) {
      await invalidateParentDocuments(queryClient, createdDocument);
    },
  });

  return {createTextDocument: mutateAsync, isCreatingTextDocument: isPending};
}

export function useUploadFileDocuments(onStateChange?: DocumentUploadStateCallback) {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: (items: UploadFileDocumentItem[]) => uploadDocumentQueue(items, onStateChange),
    retry: false,
    scope: {id: "document-uploads"},
    onSuccess(_result, items) {
      const parents = new Set(items.map((item) => item.payload.parentId));
      for (const parentId of parents) {
        queryClient.invalidateQueries({queryKey: documentKeysStore.customerDocuments(parentId)});
      }
    },
  });

  return {uploadFileDocuments: mutateAsync, isUploadingFileDocuments: isPending};
}

export interface UpdateTextDocumentVariables {
  documentId: string;
  payload: UpdateTextDocumentPayload;
}

export function useUpdateTextDocument() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: ({documentId, payload}: UpdateTextDocumentVariables) =>
      documentApiService.updateTextDocument(documentId, payload),
    async onSuccess(updatedDocument) {
      await invalidateParentDocuments(queryClient, updatedDocument);
    },
  });

  return {updateTextDocument: mutateAsync, isUpdatingTextDocument: isPending};
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: (document: DocumentResponse) =>
      documentApiService.deleteDocument(document.id).then(() => document),
    async onSuccess(deletedDocument) {
      try {
        completeDocumentUploadManifest(
          {
            companyId: deletedDocument.companyId,
            parentType: deletedDocument.parentType,
            parentId: deletedDocument.parentId,
          },
          [deletedDocument.id]
        );
      } catch {
        // La suppression serveur est acquise même si le stockage local est inaccessible.
      }
      queryClient.removeQueries({
        queryKey: documentKeysStore.downloadUrl(deletedDocument.id),
      });
      await invalidateParentDocuments(queryClient, deletedDocument);
    },
  });

  return {deleteDocument: mutateAsync, isDeletingDocument: isPending};
}

// Les URL signées expirent côté serveur après environ dix minutes.
export function useDocumentDownloadUrl(documentId: string | undefined, enabled = false) {
  const {data, isLoading, isFetching, refetch} = useQuery({
    queryKey: documentKeysStore.downloadUrl(documentId as string),
    queryFn: () => documentApiService.getDownloadUrl(documentId as string),
    enabled: enabled && !!documentId,
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000,
  });

  return {
    downloadUrl: data?.downloadUrl,
    isDownloadUrlLoading: isLoading,
    isDownloadUrlFetching: isFetching,
    refetchDownloadUrl: refetch,
  };
}

async function invalidateParentDocuments(
  queryClient: ReturnType<typeof useQueryClient>,
  document: DocumentResponse
) {
  if (document.parentType === "CUSTOMER") {
    await queryClient.invalidateQueries({
      queryKey: documentKeysStore.customerDocuments(document.parentId),
    });
    if (document.kind === "text") {
      await invalidatePageQueries(queryClient, ["customer", "infinite"]);
    }
  }
}
